import React, { useCallback, useEffect, useRef, useState } from 'react';
import Filter from '../../reusable/Client/Filter';
import AddNewClient from '../../reusable/Client/AddNewClient';
import AssignedDriver from '../../reusable/Client/AssignedDriver';
import ManageTask from '../../reusable/Client/ManageTask';
import WorkPerformanceGraph from './Graph/ClientGraph';
import ClientListTable from './ClientListTable';
import axiosInstance from 'config/axiosInstance';
import { ClientSelection } from './Utility/selection';
import { summarizeUrl } from './Utility/summary';
import DeleteClientDialog from '../../reusable/Client/dialog/DeleteClientDialog';
import { useTranslation } from 'react-i18next';
import { useSaveError } from 'reusable/useSaveError';
import Pagination from 'reusable/pagination';
import { pageToRecover } from 'reusable/pageRecovery';
import { PageSizeSelect, usePageSize } from 'reusable/PageSize';

export type { ClientSelection };

interface ClientManagementProps {
  dateRange: { start: string; end: string };
  onClientHourChange: (value: boolean) => void;
  onSelectionChange?: (selection: ClientSelection) => void;
}

interface DeleteClientData {
  id: string;
  clientName: string;
  assign_drivers: string;
  tasks: string;
}

// Helper function to convert time strings to seconds
const timeStringToSeconds = (time: string | null): number => {
  if (!time) return 0;
  // Handle case for "days hours:minutes:seconds" and "hours:minutes:seconds"
  const daysMatch = time.match(/^(\d+)\s(\d{2}):(\d{2}):(\d{2})/);
  if (daysMatch) {
    const [_, days, hours, minutes, seconds] = daysMatch.map(Number);
    return days * 86400 + hours * 3600 + minutes * 60 + seconds;
  }

  const parts = time.split(':').map(Number);
  if (parts.length === 3) {
    return parts[0] * 3600 + parts[1] * 60 + parts[2]; // hours:minutes:seconds
  }

  return 0;
};

// Helper function to convert seconds back to "days hh:mm:ss" format
const secondsToDaysHHMMSS = (totalSeconds: number): string => {
  const days = Math.floor(totalSeconds / 86400); // Calculate days
  totalSeconds %= 86400;

  const hours = Math.floor(totalSeconds / 3600); // Calculate hours
  totalSeconds %= 3600;

  const minutes = Math.floor(totalSeconds / 60); // Calculate minutes
  const seconds = totalSeconds % 60; // Remaining seconds

  // Format the time with days and ensure hours, minutes, and seconds have two digits
  return `${days} ${String(hours).padStart(2, '0')}:${String(minutes).padStart(
    2,
    '0'
  )}:${seconds.toFixed(6).padStart(9, '0')}`;
};

/** EV-493。1ページの件数を覚える localStorage のキー（候補と既定は reusable/PageSize）。 */
export const CLIENT_PAGE_SIZE_KEY = 'clientList.pageSize';

/** 409 order_conflict（openapi-schema.yaml の OrderConflict）か。 */
const isOrderConflict = (error: unknown): boolean => {
  const response = (error as { response?: { status?: number; data?: { code?: unknown } } })
    ?.response;
  return response?.status === 409 && response.data?.code === 'order_conflict';
};

const ClientManagement: React.FC<ClientManagementProps> = ({
  dateRange,
  onClientHourChange,
  onSelectionChange,
}) => {
  const { t } = useTranslation('client');
  const { showSaveError, showMessages, saveErrorPopup } = useSaveError();
  const [client, setClient] = useState<any[]>([]);
  // EV-493。一覧はサーバーでページ送りにする（以前は全件を読んでいた）。
  const [pageSize, setPageSize] = usePageSize(CLIENT_PAGE_SIZE_KEY);
  const [currentPage, setCurrentPage] = useState(1);
  const [count, setCount] = useState(0);
  // EV-493。一覧を読んだときの会社の並びの版の番号。並べ替えの PATCH で送り返し、
  // 他の管理者が並びを変えていれば 409 order_conflict を受ける（以前は全件の ID の expected_ids）。
  const [orderVersion, setOrderVersion] = useState<number | null>(null);
  const [clientHour, setClientHour] = useState<any[]>([]);
  const [clientNoHour, setClientNoHour] = useState<any[]>([]);
  const [clientNoTask, setClientNoTask] = useState<any[]>([]);
  const [draggedIndex, setDraggedIndex] = useState<number | null>(null);
  const [hoveredIndex, setHoveredIndex] = useState<number | null>(null);
  const [showDeleteClientDialog, setShowDeleteClientDialog] =
    useState<boolean>(false);
  const [deleteClient, setDeleteClient] = useState<DeleteClientData | null>(
    null
  );
  const [selectedTaskId, setSelectedTaskId] = useState<string | null>(null);
  const [selectedClientIds, setSelectedClientIds] = useState<string[]>([]);
  const [selectedClientNames, setSelectedClientNames] = useState<string[]>([]);
  const [showClientHour, setShowClientHour] = useState(true);

  const handleShowClientHourChange = (value: boolean) => {
    setShowClientHour(value);
    onClientHourChange(value);
  };

  // 読み込みの順番。後から始めた読み込みがあれば、前の読み込みの応答は捨てる
  // （期間や絞り込みを変えた後に、遅れた古い応答が一覧を上書きしないため）。
  const fetchSeq = useRef(0);
  // 一覧の状態（PR #99 レビュー）。並べ替えができるのは、最新の読み込みが成功して
  // サーバーの一覧が入っている 'ready' のときだけ。読み込み中（並べ替えの送信中・
  // 読み直し中・期間や絞り込みの読み込み中）は画面の order が確定しておらず、
  // 失敗したときはサーバーと食い違い得る。古い expected_order を送らないため止める。
  // ドラッグの判定は同じ描画の中でも最新を見たいので、ref にも同じ値を持つ。
  const [listStatus, setListStatusState] = useState<'loading' | 'ready' | 'failed'>('loading');
  const listStatusRef = useRef<'loading' | 'ready' | 'failed'>('loading');
  // 並べ替えの送信中。その間に絞り込みの読み込みが終わっても 'ready' にしない
  // （送信中の並べ替えと次の並べ替えが同時に走らないように。PR #99 レビュー）。
  const reorderPending = useRef(false);
  const setListStatus = useCallback((status: 'loading' | 'ready' | 'failed') => {
    const next = status === 'ready' && reorderPending.current ? 'loading' : status;
    listStatusRef.current = next;
    setListStatusState(next);
  }, []);

  // EV-493。一覧の URL。作業（task）・配送先（id__in）の絞り込みはサーバーで行う。
  const listUrl = useCallback(
    (page: number, size: number) => {
      let url = `/v1/client_deliveries/?page=${page}&page_size=${size}`;
      if (selectedTaskId !== null && selectedTaskId !== undefined) {
        url += `&task=${encodeURIComponent(selectedTaskId)}`;
      }
      if (selectedClientIds.length > 0) url += `&id__in=${selectedClientIds.join(',')}`;
      return url;
    },
    [selectedClientIds, selectedTaskId]
  );

  // 配送先の一覧を読み込む。結果は 'loaded'（一覧が入った）、'failed'（読めなかった）、
  // 'superseded'（後から始めた読み込みに追い越された。状態はその読み込みが決める）。
  const fetchData = useCallback(async (): Promise<'loaded' | 'failed' | 'superseded'> => {
    if (!dateRange.start || !dateRange.end) return 'failed';

    const seq = ++fetchSeq.current;
    const superseded = () => seq !== fetchSeq.current;
    setListStatus('loading');
    let listLoaded = false;
    try {
      // EV-493。表示するページだけを読む。ADM-12 の作業・配送先の絞り込みもサーバーで行う
      // （以前は全件を読み、isTargetClient で画面で絞っていた）。
      let response;
      try {
        response = await axiosInstance.get(listUrl(currentPage, pageSize));
      } catch (error) {
        // EV-460。ページの外（404）なら、エラーにせず1ページ目を読む。
        const recover = superseded() ? null : pageToRecover(currentPage, pageSize, { error });
        if (recover !== null) {
          setCurrentPage(recover);
          return 'superseded';
        }
        throw error;
      }
      if (superseded()) return 'superseded';
      const rows: any[] = response.data.results ?? [];
      // EV-460。今のページが無くなっていたら（空のページ・件数あり）、最後の有効なページを読む。
      const recover = pageToRecover(currentPage, pageSize, {
        count: response.data.count,
        rows: rows.length,
      });
      if (recover !== null) {
        setCurrentPage(recover);
        return 'superseded';
      }
      setClient(rows);
      setCount(response.data.count ?? rows.length);
      setOrderVersion(response.data.order_version ?? null);
      setListStatus('ready');
      listLoaded = true;

      // EV-493。時間の集計とグラフは、表示しているページの配送先の分だけ取る（id__in）。
      // 作業は task_ids（ADM-12 / ADM-21。CSV と同じ URL の作り）。ページが空なら取らない
      // （id__in を付けないと全件の集計になる）。
      if (rows.length === 0) {
        setClientHour([]);
        setClientNoTask([]);
        setClientNoHour([]);
        return 'loaded';
      }
      const summarySelection = {
        taskId: selectedTaskId,
        clientIds: rows.map((row) => String(row.id)),
      };
      const responseHourUrl = summarizeUrl('with_tasks', summarySelection, dateRange);
      const responseHour = await axiosInstance.get(responseHourUrl);
      if (superseded()) return 'superseded';
      const hourRows = responseHour.data;
      setClientHour(hourRows);

      // Sum up the hour_spent for each client and include id, name, order, and formatted time
      const clientTaskSums = hourRows.map((client: any) => {
        const totalSeconds = client.summarize.reduce(
          (sum: number, task: any) => {
            return sum + timeStringToSeconds(task.hour_spent);
          },
          0
        );

        return {
          id: client.id,
          name: client.name,
          order: client.order,
          hour_spent: secondsToDaysHHMMSS(totalSeconds),
        };
      });

      setClientNoTask(clientTaskSums);

      const responseNoHourUrl = summarizeUrl('without_tasks', summarySelection, dateRange);
      const responseNoHour = await axiosInstance.get(responseNoHourUrl);
      if (superseded()) return 'superseded';
      setClientNoHour(responseNoHour.data);
    } catch (error) {
      console.error('Failed to fetch client data', error);
      if (superseded()) return 'superseded';
      // 最新の読み込みで一覧を読めなかった。並べ替えの後かどうか（最初の読み込みを
      // 含む）に関係なく、画面の order はサーバーと食い違い得るので並べ替えを止める。
      // 一覧は読めて集計だけが失敗したときは、並びはサーバーと合っている。
      if (!listLoaded) setListStatus('failed');
    }
    return listLoaded ? 'loaded' : 'failed';
  }, [currentPage, dateRange, listUrl, pageSize, selectedTaskId, setListStatus]);

  // PR #99 レビュー。絞り込み（期間・作業・配送先）の世代と、最新の絞り込みの fetchData。
  // 並べ替えの送信を待つ間に絞り込みが変わっても、保存後の読み直しは最新の絞り込みで
  // 行う。古い描画の fetchData で読み直すと、後から番号を得て新しい一覧と集計を
  // 前の絞り込みで上書きしていた。
  const filterGeneration = useRef(0);
  const latestFetchData = useRef(fetchData);
  latestFetchData.current = fetchData;

  useEffect(() => {
    filterGeneration.current += 1;
    fetchData();
  }, [fetchData]);

  const handleDragStart = (index: number) => {
    setDraggedIndex(index);
  };

  const handleDragEnter = (index: number) => {
    if (draggedIndex === null || draggedIndex === index) return;

    setHoveredIndex(index);
  };

  // 1件を new_order へ移す（ADM-14）。並べ替えの送信から、確定した順番の読み直しと
  // 競合の案内までを、ドラッグと「順番を指定して移動」で共通にする。
  // previous: 画面を先に動かしたとき（ドラッグ）の移動前の一覧。失敗して読み直せなければ戻す。
  // targetPage: 成功したら読むページ（移した先の順番があるページ）。
  // generation: 操作を始めたときの絞り込み・ページの世代。移動先の行を読む間にページや絞り込みが
  // 変わっていたら、移した先のページへは移らず、今のページを読み直す（#430 の Codex の指摘 4392113471）。
  const submitMove = async (
    item: any,
    newOrder: number,
    {
      previous,
      targetPage,
      generation = filterGeneration.current,
    }: { previous?: any[]; targetPage?: number; generation?: number } = {}
  ) => {
    let conflict = false;
    let saveFailed = false;
    try {
      // T02 ステップ2・EV-493。一覧を読んだときの並びの版の番号を送る。サーバーは並びが
      // 変わる操作（並べ替え・追加・削除・復元）のたびに番号を上げるので、他の管理者が
      // 別の行だけを並べ替えた場合も 409 order_conflict を返す（PR #99 の expected_ids は
      // 会社の並び全体の ID を要し、全件の読み込みが必要だった）。
      await axiosInstance.patch(`/v1/client_deliveries/${item.id}/change_order/`, {
        new_order: newOrder,
        order_version: orderVersion,
      });
    } catch (error) {
      console.error('Failed to update client order on the server', error);
      saveFailed = true;
      conflict = isOrderConflict(error);
      if (!conflict) showSaveError(error);
    }
    reorderPending.current = false;
    if (
      !saveFailed &&
      targetPage !== undefined &&
      targetPage !== currentPage &&
      generation === filterGeneration.current
    ) {
      // 移した先のページを読む（ページが変わると fetchData が読み直し、並べ替えを
      // 再び受け付けるかもその読み込みが決める）。
      setCurrentPage(targetPage);
      return;
    }
    // 成功・失敗どちらでも、サーバーで確定した順番を最新の絞り込みで取り直す。
    // 並べ替えを再び受け付けるかは fetchData が一覧の状態で決める。
    const reloaded = await latestFetchData.current();
    if (
      previous &&
      saveFailed &&
      reloaded === 'failed' &&
      generation === filterGeneration.current
    ) {
      // 並べ替えは反映されていないのに、画面には移動後の並びが残る。移動前に戻す。
      // 絞り込みが変わっていたら、この closure の一覧は前の絞り込みのものなので戻さない。
      setClient(previous);
    }
    if (conflict) {
      // 競合は入力の誤りではないので、画面の言語で知らせる。
      // 「読み直した」と言えるのは、読み直しが成功したときだけ（PR #99 レビュー）。
      showMessages([
        reloaded === 'loaded'
          ? t('ui.order-conflict')
          : reloaded === 'failed'
          ? t('ui.order-conflict-reload-failed')
          : t('ui.order-conflict-reloading'),
      ]);
    }
  };

  const handleDragEnd = async () => {
    if (
      listStatusRef.current === 'ready' &&
      draggedIndex !== null &&
      hoveredIndex !== null &&
      draggedIndex !== hoveredIndex
    ) {
      // ADM-14。サーバーの change_order は「1件を new_order へ移動し、間を ±1 ずらす」。
      // 2件の order を入れ替えて2本同時に送ると、離れた2件では入れ替えにならず、
      // 同時実行で順番が重複・欠番になり得た。移動は1本だけ送る。
      // EV-493。ドラッグはページの中だけ。ページをまたぐときは「順番を指定して移動」。
      const draggedItem = client[draggedIndex];
      const newOrder = client[hoveredIndex].order;

      // 画面もサーバーと同じ「移動」で並べる（order は応答の取り直しで確定させる）。
      const updatedClient = [...client];
      updatedClient.splice(draggedIndex, 1);
      updatedClient.splice(hoveredIndex, 0, draggedItem);
      setClient(updatedClient);
      setDraggedIndex(null);
      setHoveredIndex(null);

      // 前の移動が確定し、一覧を読み直すまで次の移動は受け付けない
      // （画面の order は確定しておらず、同時実行で順番が崩れるため）。
      reorderPending.current = true;
      setListStatus('loading');
      await submitMove(draggedItem, newOrder, { previous: client });
      return;
    }
    setDraggedIndex(null);
    setHoveredIndex(null);
  };

  // EV-493。「順番を指定して移動」。position は今の一覧（絞り込み後）での1からの順番。
  // その順番にある行の order へ移す（ドラッグで行の上に落としたときと同じ移動）。
  const handleMoveToPosition = async (item: any, position: number) => {
    if (listStatusRef.current !== 'ready') return;
    const offset = (currentPage - 1) * pageSize;
    const index = client.findIndex((row) => row.id === item.id);
    if (index < 0 || offset + index + 1 === position) return;
    const generation = filterGeneration.current;
    reorderPending.current = true;
    setListStatus('loading');
    let target: any = null;
    const localIndex = position - offset - 1;
    if (localIndex >= 0 && localIndex < client.length) {
      target = client[localIndex];
    } else {
      // 別のページの順番。その順番の行を1件だけ読む（同じ絞り込み）。読んでいる間に並びが
      // 変わっていても、送る番号は一覧を読んだときのものなので、サーバーが 409 で断る。
      try {
        const response = await axiosInstance.get(listUrl(position, 1));
        target = response.data.results?.[0] ?? null;
      } catch (error) {
        console.error('Failed to fetch the client at the position', error);
        showSaveError(error);
        reorderPending.current = false;
        await latestFetchData.current();
        return;
      }
    }
    if (!target) {
      // その順番の行が無くなっていた（他の管理者が配送先を減らした）。競合と同じく読み直す。
      reorderPending.current = false;
      const reloaded = await latestFetchData.current();
      showMessages([
        reloaded === 'loaded'
          ? t('ui.order-conflict')
          : reloaded === 'failed'
          ? t('ui.order-conflict-reload-failed')
          : t('ui.order-conflict-reloading'),
      ]);
      return;
    }
    await submitMove(item, target.order, {
      targetPage: Math.ceil(position / pageSize),
      generation,
    });
  };

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  const handlePageSizeChange = (size: number) => {
    setPageSize(size);
    setCurrentPage(1);
  };

  const handleDeleteClick = (client: any) => {
    setDeleteClient({
      id: client.id,
      clientName: client.name,
      // 人数は画面の言語の数え方で出す（以前は「2 people」と英語の固定の文字列だった）。
      assign_drivers: t('table.driver_count', { count: client.assign_drivers.length }),
      tasks: client.tasks.length.toString(),
    });
    setShowDeleteClientDialog(true);
  };

  // EV-493。絞り込みを変えたら1ページ目から読む。
  const handleTaskSelect = (taskId: string | null, taskName: string) => {
    setSelectedTaskId(taskId);
    setCurrentPage(1);
  };

  const handleClientSelect = (clientIds: string[], clientNames: string[]) => {
    setSelectedClientIds(clientIds);
    setSelectedClientNames(clientNames);
    setCurrentPage(1);
  };

  // ADM-12。CSV にも同じ選択を渡す。
  useEffect(() => {
    onSelectionChange?.({ taskId: selectedTaskId, clientIds: selectedClientIds });
  }, [onSelectionChange, selectedTaskId, selectedClientIds]);

  return (
    <main className="flex flex-col bg-white">
      <div className="flex flex-col px-4 py-2">
        <div className="flex flex-row justify-between items-center">
          <div className="flex flex-col">
            <h4 className="text-subtitle-1 font-medium">{t('graph.title')}</h4>
          </div>
          <div className="flex flex-row items-center space-x-4">
            <Filter
              onTaskSelect={handleTaskSelect}
              onClientSelect={handleClientSelect}
            />
            <AddNewClient />
            <AssignedDriver />
            <ManageTask />
          </div>
        </div>
        <div>
          <WorkPerformanceGraph
            clientHour={clientHour}
            clientNoHour={clientNoTask}
            onShowClientHourChange={handleShowClientHourChange}
            showClientHour={showClientHour}
          />
        </div>
      </div>
      <div className="flex flex-col p-4 space-y-2">
        {listStatus === 'failed' && (
          <div role="alert" className="flex flex-row items-center gap-2 text-red-600">
            <p>{t('ui.order-reload-failed')}</p>
            <button
              type="button"
              className="rounded-md border px-4 py-2 text-primary/300"
              onClick={() => fetchData()}
            >
              {t('ui.retry', { ns: 'common' })}
            </button>
          </div>
        )}
        <div className="flex flex-row">
          <ClientListTable
            clientData={client}
            positionOffset={(currentPage - 1) * pageSize}
            totalCount={count}
            onDragStart={handleDragStart}
            onDragEnter={handleDragEnter}
            onDragEnd={handleDragEnd}
            onDeleteClick={handleDeleteClick}
            onMoveToPosition={handleMoveToPosition}
          />
        </div>
        {/* EV-493。1ページの件数（20・50・100）とページ送り。 */}
        <PageSizeSelect id="client-page-size" value={pageSize} onChange={handlePageSizeChange} />
        <Pagination
          allRows={count}
          rowsPerPage={pageSize}
          currentPage={currentPage}
          onPageChange={handlePageChange}
        />
      </div>
      {showDeleteClientDialog && deleteClient && (
        <DeleteClientDialog
          id={deleteClient.id}
          name={deleteClient.clientName}
          isOpen={showDeleteClientDialog}
          onClose={() => setShowDeleteClientDialog(false)}
          fields={[
            { label: t('table.client_id'), value: deleteClient.id },
            { label: t('table.client_name'), value: deleteClient.clientName },
            { label: t('table.assignees'), value: deleteClient.assign_drivers },
            { label: t('table.task_counts'), value: deleteClient.tasks },
          ]}
        />
      )}
      {saveErrorPopup}
    </main>
  );
};

export default ClientManagement;
